// Mirrors the existing dashboard/info field contract. Ticket face values and
// recorded payments are deliberately separate; no mock data is used here.
export const transactionColors = [
  "#3b82f6",
  "#10b981",
  "#8b5cf6",
  "#f59e0b",
  "#f43f5e",
];
export function number(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}
export function buildOverview(data = {}) {
  const n = (key) => number(data[key]);
  const rows = [
    ["pawn", "numOfPawn", "pawnValue", null, null, 3],
    ["renew", "numOfInterest", "interestPawnValue", "interestAmount", null, 4],
    ["increase", "numOfInc", "incPawnValue", "incInterestAmount", "incDiff", 5],
    ["reduce", "numOfDec", "decPawnValue", "decInterestAmount", "decDiff", 6],
    [
      "redeem",
      "numOfRedeem",
      "redeemPawnValue",
      "redeemInterestAmount",
      null,
      7,
    ],
  ].map(([key, count, value, interest, diff, history], i) => ({
    key,
    count: n(count),
    value: n(value),
    interest: interest ? n(interest) : 0,
    diff: diff ? n(diff) : 0,
    history,
    color: transactionColors[i],
    migration: false,
  }));
  const migrations = [
    ["renew", "numOfInterestMigration", "interestPawnValueMigration", 8, 1],
    ["increase", "numOfIncMigration", "incPawnValueMigration", 9, 2],
    ["reduce", "numOfDecMigration", "decPawnValueMigration", 10, 3],
  ].map(([key, count, value, history, color]) => ({
    key: key + "Migration",
    labelKey: key,
    count: n(count),
    value: n(value),
    interest: null,
    diff: 0,
    history,
    color: transactionColors[color],
    migration: true,
  }));
  const sum = (items, field) =>
    items.reduce((total, row) => total + number(row[field]), 0);
  const migrationValue = sum(migrations, "value");
  const incoming = sum(rows.slice(0, 4), "value") + migrationValue;
  const outgoing =
    rows[1].value +
    rows[2].value -
    rows[2].diff +
    rows[3].value +
    rows[3].diff +
    rows[4].value;
  const incomingCount =
    sum(rows.slice(0, 4), "count") + sum(migrations, "count");
  const outgoingCount = sum(rows.slice(1), "count");
  const interest = sum(rows, "interest");
  const receive = n("receiveAmount"),
    pay = n("payAmount");
  return {
    rows,
    migrations,
    incoming,
    outgoing,
    incomingCount,
    outgoingCount,
    interest,
    ticketNet: outgoing - incoming,
    stockNet: incomingCount - outgoingCount,
    receive,
    pay,
    cashNet: receive - pay,
    migrationValue,
    migrationCount: sum(migrations, "count"),
    count: sum(rows, "count"),
    value: sum(rows, "value"),
    pawnIncrease: rows[0].value + rows[2].value,
    others: rows[1].value + rows[3].value + rows[4].value,
    donuts: [
      {
        key: "incoming",
        total: incoming,
        rows: rows
          .slice(0, 4)
          .map((row, i) => ({
            ...row,
            value: row.value + (i === 0 ? migrationValue : 0),
          })),
      },
      // Match the displayed old-ticket total: increases remove the extra
      // principal; reductions add back the principal paid down.
      {
        key: "outgoing",
        total: outgoing,
        rows: rows
          .slice(1)
          .map((row) => ({
            ...row,
            value:
              row.key === "increase"
                ? row.value - row.diff
                : row.key === "reduce"
                  ? row.value + row.diff
                  : row.value,
          })),
      },
      {
        key: "interest",
        total: interest,
        rows: rows.slice(1).map((row) => ({ ...row, value: row.interest })),
      },
    ],
  };
}

export function buildCardDetail(model, key) {
  const regularRows = model.rows || [];
  const migrationRows = model.migrations || [];

  if (key === "incoming") {
    return {
      metrics: [
        { key: "ticketValue", value: model.incoming, format: "money" },
        { key: "count", value: model.incomingCount, format: "count" },
      ],
      rows: [...regularRows.slice(0, 4), ...migrationRows],
    };
  }

  if (key === "outgoing") {
    return {
      metrics: [
        { key: "ticketValue", value: model.outgoing, format: "money" },
        { key: "count", value: model.outgoingCount, format: "count" },
      ],
      rows: regularRows.slice(1).map((row) => ({
        ...row,
        displayValue:
          row.key === "increase"
            ? row.value - row.diff
            : row.key === "reduce"
              ? row.value + row.diff
              : row.value,
      })),
    };
  }

  if (key === "interest") {
    return {
      metrics: [
        { key: "interestColumn", value: model.interest, format: "money" },
        {
          key: "count",
          value: regularRows.slice(1).reduce((sum, row) => sum + row.count, 0),
          format: "count",
        },
      ],
      rows: regularRows.slice(1),
    };
  }

  return {
    metrics: [
      { key: "outgoing", value: model.outgoing, format: "money" },
      { key: "incoming", value: model.incoming, format: "money" },
      { key: "ticketNet", value: model.ticketNet, format: "money" },
      { key: "stockNet", value: model.stockNet, format: "signedCount" },
    ],
    rows: [],
  };
}
export function day(value = new Date()) {
  const d = new Date(value);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
export function presetRange(preset, now = new Date()) {
  const to = day(now),
    from = day(now);
  if (preset === "week") from.setDate(from.getDate() - 6);
  if (preset === "month") from.setDate(1);
  return { from, to };
}
export function validateRange(from, to, now = new Date()) {
  if (
    !(from instanceof Date) ||
    !(to instanceof Date) ||
    !Number.isFinite(+from) ||
    !Number.isFinite(+to)
  )
    return "required";
  if (day(from) > day(to)) return "reversed";
  if (day(to) > day(now)) return "future";
  const min = day(now);
  min.setMonth(min.getMonth() - 3);
  if (day(from) < min) return "limit";
  return "";
}
export function donutSegments(rows) {
  const total = rows.reduce((s, r) => s + Math.max(0, number(r.value)), 0);
  let start = 0;
  return rows
    .filter((r) => number(r.value) > 0)
    .map((row) => {
      const percent = (row.value / total) * 100,
        angle = ((start + percent / 2) * Math.PI) / 50 - Math.PI / 2;
      const result = {
        ...row,
        percent,
        start,
        x: 130 + Math.cos(angle) * 78,
        y: 120 + Math.sin(angle) * 78,
      };
      start += percent;
      return result;
    });
}
